import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Input, isValidEmail, Snackbar } from '@slite/components'
import { CheckMarkRoundIcon, ErrorIcon } from '@slite/icons'

import { ButtonSubmit } from '../../components/ButtonSubmit'
import { FormField } from '../../components/FormField'
import { useRoutes } from '../../routes'
import { track } from '../../utils/analytics'

type FormState = 'idle' | 'submitting' | 'success'

/* Off-screen rather than display:none — bots skip hidden inputs. */
const decoyFieldStyle: React.CSSProperties = {
  position: 'absolute',
  left: -10000,
  width: 1,
  height: 1,
  overflow: 'hidden',
}

export const NewsletterForm = () => {
  const intl = useIntl()
  const routes = useRoutes()
  const [formContent, setFormContent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    referral: '',
  })
  const [formState, setFormState] = useState<FormState>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrorMessage('')

    if (!isValidEmail(formContent.email.trim())) {
      setErrorMessage(
        intl.formatMessage({
          id: 'website.newsletter.form.errors.email',
          defaultMessage: 'Please enter a valid email',
        })
      )
      return
    }

    setFormState('submitting')

    try {
      const response = await fetch('/website/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formContent),
      })
      if (!response.ok) throw new Error(`Signup failed: ${response.status}`)
    } catch {
      setFormState('idle')
      setErrorMessage(
        intl.formatMessage({
          id: 'website.newsletter.form.errors.submit',
          defaultMessage: 'Something went wrong, please try again.',
        })
      )
      return
    }

    track({ eventName: 'WEBSITE_NEWSLETTER_SUBSCRIBED' })
    setFormState('success')
  }

  if (formState === 'success') {
    return (
      <div
        role="status"
        className="flexbox flexbox-column flexbox-align-center flexbox-gap-12 text-align-center padding-vertical-24"
      >
        <CheckMarkRoundIcon size={32} className="success-content" />
        <div className="font-size-4 font-family-title font-weight-medium">
          <FormattedMessage
            id="website.newsletter.success.title"
            defaultMessage="You're on the list"
          />
        </div>
        <p className="font-size-6 line-height-large shade-20">
          <FormattedMessage
            id="website.newsletter.success.body"
            defaultMessage="Keep an eye on your inbox — the next issue is on its way."
          />
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flexbox flexbox-column flexbox-gap-16 width-full position-relative"
    >
      {errorMessage && (
        <Snackbar icon={ErrorIcon} radiusSize="large" variant="danger">
          {errorMessage}
        </Snackbar>
      )}
      <div className="flexbox flexbox-gap-12">
        <FormField
          label={intl.formatMessage({
            id: 'website.newsletter.form.firstNameLabel',
            defaultMessage: 'First name',
          })}
        >
          <Input
            type="text"
            name="firstName"
            autoComplete="given-name"
            wrapperClassName="background-shade-100"
            size="large"
            placeholder={intl.formatMessage({
              id: 'website.newsletter.form.firstNamePlaceholder',
              defaultMessage: 'Roger',
            })}
            value={formContent.firstName}
            onChange={(e) =>
              setFormContent({ ...formContent, firstName: e.target.value })
            }
            required={true}
          />
        </FormField>
        <FormField
          label={intl.formatMessage({
            id: 'website.newsletter.form.lastNameLabel',
            defaultMessage: 'Last name',
          })}
        >
          <Input
            type="text"
            name="lastName"
            autoComplete="family-name"
            wrapperClassName="background-shade-100"
            size="large"
            placeholder={intl.formatMessage({
              id: 'website.newsletter.form.lastNamePlaceholder',
              defaultMessage: 'Smith',
            })}
            value={formContent.lastName}
            onChange={(e) =>
              setFormContent({ ...formContent, lastName: e.target.value })
            }
            required={true}
          />
        </FormField>
      </div>
      <FormField
        label={intl.formatMessage({
          id: 'website.newsletter.form.emailLabel',
          defaultMessage: 'Email',
        })}
      >
        <Input
          type="email"
          name="email"
          autoComplete="email"
          wrapperClassName="background-shade-100"
          size="large"
          placeholder={intl.formatMessage({
            id: 'website.newsletter.form.emailPlaceholder',
            defaultMessage: 'roger@acme.inc',
          })}
          value={formContent.email}
          onChange={(e) =>
            setFormContent({ ...formContent, email: e.target.value })
          }
          required={true}
        />
      </FormField>
      <div aria-hidden="true" style={decoyFieldStyle}>
        <input
          type="text"
          name="referral"
          tabIndex={-1}
          autoComplete="off"
          value={formContent.referral}
          onChange={(e) =>
            setFormContent({ ...formContent, referral: e.target.value })
          }
        />
      </div>
      <ButtonSubmit
        disabled={formState === 'submitting'}
        variant="primary"
        size="huge"
        className="width-full margin-top-8"
      >
        {intl.formatMessage({
          id: 'website.newsletter.form.submit',
          defaultMessage: 'Subscribe',
        })}
      </ButtonSubmit>
      <p className="font-size-8 shade-20 text-align-center line-height-large">
        <FormattedMessage
          id="website.newsletter.form.consent"
          defaultMessage="By subscribing you agree to receive emails from Slite and to our <link>Privacy Policy</link>. Unsubscribe anytime."
          values={{
            link: (text: React.ReactNode) => (
              <a
                href={routes.legal.privacy()}
                target="_blank"
                rel="noreferrer"
                className="underline shade-10"
              >
                {text}
              </a>
            ),
          }}
        />
      </p>
    </form>
  )
}
